import classname from "classnames";
import { useEffect, useRef } from "react";
import PropTypes from 'prop-types';
import InputWithTooltipPlaceholder from './InputWithTooltipPlaceholder';
import { cleanDescription } from '@/utils/clean-description';

function getClassName(error) {
	return classname({
		"eform-input": true,
		"eform-input-standard": true,
		"eform-input-error": error ? true : false,
	});
}

const CellNumberComponent = ({
	error,
	isReadOnly,
	schema,
	payload,
	parentProps,
	inputProps,
	setInputProps,
	updatePayload,
	validateSchema,
	getFieldValue,
}) => {
	// Determine if the field is integer type
	const isIntegerType = () => {
		if (!schema) return false;

		// Check for explicit integer type
		if (schema.type === "integer") return true;

		// Check for array type that includes integer
		if (Array.isArray(schema.type) && schema.type.includes("integer")) return true;

		return false;
	};

	useEffect(() => {
		const fieldValue = getFieldValue(payload, parentProps);
		const valueProp = fieldValue !== null && fieldValue !== undefined && fieldValue !== "" ? { value: fieldValue } : {};

		setInputProps({
			...inputProps,
			...valueProp,
			disabled: isReadOnly,
			placeholder: isReadOnly ? null : cleanDescription(schema.description) || schema.title || "",
			// For integer fields, use 'text' type with pattern to prevent 'e' input
			// For double/float fields, use 'number' type but we'll handle 'e' in the onChange
			type: isIntegerType() ? "text" : "number",
			// For integer fields, add pattern attribute to only allow digits
			pattern: isIntegerType() ? "-?\\d*" : undefined,
			value: getFieldValue(payload, parentProps) === null ? "" : getFieldValue(payload, parentProps),
		});
	}, [payload, isReadOnly, schema]);

	function onChange(e) {
		if (isReadOnly) {
			return;
		}
		let value = e.target.value;

		// Handle empty value
		if (value === "") {
			setInputProps({ ...inputProps, value: undefined });
			updatePayload(null);
			validateSchema(null);
			return;
		}

		// Different validation for integer vs double/float
		if (isIntegerType()) {
			// For integer fields, only allow digits and optional negative sign
			const integerRegex = /^-?\d*$/;
			if (!integerRegex.test(value)) {
				// Invalid input for integer, don't update
				return;
			}

			// Convert to number if it's a valid integer
			if (value !== "-") { // Allow negative sign as intermediate input
				value = Number(value);
				setInputProps({ ...inputProps, value: value });
			}
		} else {
			// For double/float fields, allow decimal points but not 'e' notation
			const doubleRegex = /^-?\d*\.?\d*$/;
			if (!doubleRegex.test(value)) {
				// If it contains 'e', reject it
				if (/[eE]/.test(value)) {
					return;
				}
			}

			// Convert to number if it's a valid double/float
			if (value !== "-" && value !== "-." && !value.endsWith(".")) {
				value = Number(value);
				setInputProps({ ...inputProps, value: value });
			}
		}

		updatePayload(value);
		validateSchema(value);
	}

	return (
		<InputWithTooltipPlaceholder
			{...inputProps}
			onChange={onChange}
			className={getClassName(error)}
		/>
	);
};

CellNumberComponent.propTypes = {
    error: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    schema: PropTypes.object,
    payload: PropTypes.object,
    parentProps: PropTypes.array,
    inputProps: PropTypes.object,
    setInputProps: PropTypes.func,
    updatePayload: PropTypes.func,
    validateSchema: PropTypes.func,
    getFieldValue: PropTypes.func
};

export default CellNumberComponent;
